import { currentUser } from "@homebound/auth-components";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDeepLink } from "src/hooks/useDeepLink";
import { AppProviders } from "src/routing/components/AppProviders";
import { AuthenticatedShell } from "src/routing/components/AuthenticatedShell";
import { RouteEnvironmentProviders } from "src/routing/components/RouteEnvironment";

/** Auth gate for migrated domains.
 *
 * Role: data router root `Component` on each domain `RouteObject` in `src/routing/domains/*.ts`.
 * Renders `<Outlet/>` for domain chrome + pages. Does not define URLs itself.
 *
 * Legacy URLs never mount this — they fall through `Router.tsx` to `src/App.tsx` instead.
 * See `src/routing/README.md`.
 */
export function AppShell() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    onLoad().catch(console.error);
    async function onLoad() {
      const { data: user } = await currentUser();
      setIsAuthenticated(user !== undefined);
      setIsAuthenticating(false);
    }
  }, []);

  useDeepLink(isAuthenticated, isAuthenticating);

  // Authenticated AppShell users are already on their destination; clear any stale deepLink left
  // from the auth-loading window (or a prior session) so the dashboard redirect doesn't hijack navigation.
  useEffect(() => {
    if (!isAuthenticating && isAuthenticated) {
      localStorage.removeItem("deepLink");
    }
  }, [isAuthenticating, isAuthenticated]);

  if (isAuthenticating) {
    return <div />;
  }

  return (
    <RouteEnvironmentProviders>
      {isAuthenticated ? (
        <AppProviders>
          <AuthenticatedShell />
        </AppProviders>
      ) : (
        <Navigate to="/signIn" replace />
      )}
    </RouteEnvironmentProviders>
  );
}
